import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { MediaAsContents } from '@syconium/little-miss-figgy'

import { TrackingLocationProvider } from '../../../../../../app/_providers/TrackingLocationProvider.client'
import { FILTERS_QUERY_PARAM } from '../../../../../../brunswick/constants'
import { CollectionViewProductImpressions } from '../../../../../../lib/hooks/useCollectionViewProductImpressions'
import { deriveCollectionItemFilterInput } from '../../../../../../lib/utils/collection-filters'
import { Collection } from '../../Collection'
import { useCollectionGridSectionCollections } from '../../hooks/useCollectionGridSectionItems'
import {
  UseEditorializedCollectionsProps,
  useEditorializedCollections,
} from '../../hooks/useEditorializedCollectionItems'

import { Section } from './styles'
import { NoResultsTile } from './Tiles/NoResultsTile'

import type { CollectionGridSection as CollectionGridSectionQueryResults } from '../../../../../../types/graphql/collection-view'

export type CollectionGridSectionProps = CollectionGridSectionQueryResults & {
  viewHandle: string
}

type CollectionEditorialInfo = Omit<
  NonNullable<CollectionGridSectionProps['editorializedCollections']>[number],
  '__typename' | 'id' | 'handle'
>

export const CollectionGridSection = ({
  viewHandle,
  filtersEnabled,
  filterConfig,
  id: _sectionId,
  visualFiltersSection,
  visualFiltersSectionV2,
  editorializedCollections: editorializedCollectionsMetadata,
}: CollectionGridSectionProps) => {
  const {
    query: {
      [FILTERS_QUERY_PARAM]: collectionFiltersQueryParam,
      category: legacyCategoryQueryParam,
      color: legacyColorQueryParam,
      fit: legacyFitQueryParam,
    },
  } = useRouter()

  const renderV2VisualFilters = visualFiltersSectionV2?.id
  const renderV1VisualFilters = !renderV2VisualFilters && visualFiltersSection?.id

  const editorializedCollectionsMetadataMap = useMemo(() => {
    const editorializedCollectionsMetadataMap: Record<string, CollectionEditorialInfo> = {}

    editorializedCollectionsMetadata.forEach(next => {
      editorializedCollectionsMetadataMap[next.handle] = next
    })

    return editorializedCollectionsMetadataMap
  }, [editorializedCollectionsMetadata])

  const collectionHandles = useMemo(() => {
    return editorializedCollectionsMetadata.map(editorializedCollectionMetadata => {
      return editorializedCollectionMetadata.handle
    })
  }, [editorializedCollectionsMetadata])

  const { filterOptions, selectedFilters } = useMemo(() => {
    return deriveCollectionItemFilterInput({
      filterConfig,
      collectionFiltersQueryParam,
      legacyCategoryQueryParam,
      legacyColorQueryParam,
      legacyFitQueryParam,
    })
  }, [
    filterConfig,
    collectionFiltersQueryParam,
    legacyCategoryQueryParam,
    legacyColorQueryParam,
    legacyFitQueryParam,
  ])

  const {
    data: _data,
    previousData,
    fetchMore: loadMoreItems,
    status,
  } = useCollectionGridSectionCollections({
    collectionHandles,
    filterOptions,
  })
  const data = _data ?? previousData

  const totalItemCount = data?.numItems ?? 0
  const loadingInitial = (status === 'pending' || status === 'idle') && !data
  const loadingMore = loadingInitial || status === 'pending-more'

  const filters = data?.filters

  const collections: UseEditorializedCollectionsProps['collections'] = useMemo(() => {
    return (
      data?.collections.map(collection => {
        const editorializedCollection: UseEditorializedCollectionsProps['collections'][number] = {
          collectionScope: {
            collectionHandle: collection.handle,
            pricingDisplay:
              editorializedCollectionsMetadataMap[collection.handle]?.pricingDisplay ??
              'priceRange',
          },
          products: collection.items.nodes.map(node => node.variantGroup),
          editorialCellPlacements:
            editorializedCollectionsMetadataMap[collection.handle]?.editorialCellPlacements ?? [],
          editorialRows:
            editorializedCollectionsMetadataMap[collection.handle]?.editorialRows ?? [],
          editorialOverrides:
            editorializedCollectionsMetadataMap[collection.handle]?.editorials ?? [],
          title: editorializedCollectionsMetadataMap[collection.handle]?.title ?? undefined,
          subtitle: editorializedCollectionsMetadataMap[collection.handle]?.subtitle ?? undefined,
          numItems: collection.numItems,
        }
        return editorializedCollection
      }) ?? []
    )
  }, [data?.collections, editorializedCollectionsMetadataMap])

  const editorializedCollections = useEditorializedCollections({
    collections,
    ignoreEditorials: selectedFilters.length > 0 && filtersEnabled,
  })
  const isFirstTileTitle = useMemo(
    () => editorializedCollections[0]?.items.desktop[0]?.__typename === 'TitleTile',
    [editorializedCollections]
  )

  const skusViewed = useMemo(() => {
    const skusLoaded: string[] = []
    data?.collections.forEach(collection =>
      collection.items.nodes.forEach(node => {
        const sku = node.variantGroup.defaultShopifyProduct.defaultShopifyVariant.sku
        if (sku) skusLoaded.push(sku)
      })
    )
    return skusLoaded
  }, [data?.collections])

  return (
    <Section data-testid='collection-grid-section'>
      <CollectionViewProductImpressions viewHandle={viewHandle} skusViewed={skusViewed} />
      <Collection.Wrapper
        collectionHandle={viewHandle}
        totalItemsCount={totalItemCount}
        filters={filtersEnabled ? filters : undefined}
        filterConfig={filterConfig}
      >
        {filtersEnabled && renderV2VisualFilters ? (
          <Collection.VisualFilters {...visualFiltersSectionV2} />
        ) : null}

        {filtersEnabled && renderV1VisualFilters ? (
          <Collection.VisualFilters {...visualFiltersSection} />
        ) : null}

        <Collection.Controls isLoading={loadingInitial} />

        <Collection.Grid isLoading={loadingInitial} hasVerticalMarginDesktop={!isFirstTileTitle}>
          <MediaAsContents lessThan='md'>
            {editorializedCollections.map(editorializedCollection => {
              return (
                <TrackingLocationProvider
                  key={`${editorializedCollection.collectionScope.collectionHandle}:mobile`}
                  moduleName={editorializedCollection.collectionScope.collectionHandle}
                >
                  {editorializedCollection.items.mobile.map((item, itemIndex) => {
                    return (
                      <TrackingLocationProvider
                        key={`${editorializedCollection.collectionScope.collectionHandle}:mobile:${item.id}`}
                        position={itemIndex + 1}
                      >
                        <Collection.GridItem item={item} index={itemIndex} />
                      </TrackingLocationProvider>
                    )
                  })}
                </TrackingLocationProvider>
              )
            })}
          </MediaAsContents>

          <MediaAsContents greaterThanOrEqual='md'>
            {editorializedCollections.map(editorializedCollection => {
              return (
                <TrackingLocationProvider
                  key={`${editorializedCollection.collectionScope.collectionHandle}:desktop`}
                  moduleName={editorializedCollection.collectionScope.collectionHandle}
                >
                  {editorializedCollection.items.desktop.map((item, itemIndex) => {
                    return (
                      <TrackingLocationProvider
                        key={`${editorializedCollection.collectionScope.collectionHandle}:desktop:${item.id}`}
                        position={itemIndex + 1}
                      >
                        <Collection.GridItem item={item} index={itemIndex} />
                      </TrackingLocationProvider>
                    )
                  })}
                </TrackingLocationProvider>
              )
            })}
          </MediaAsContents>

          {totalItemCount === 0 && !loadingMore ? <NoResultsTile /> : null}
        </Collection.Grid>

        <Collection.Footer loadMore={loadMoreItems} />
      </Collection.Wrapper>
    </Section>
  )
}
